import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  Paper,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { getDownloadURL, ref } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { getUserFromSessionStorage } from '../../core/services/auth';
import {
  castVote,
  getActiveElection,
  getCandidatesByPosition,
  getPolls,
  getPositions,
  hasUserVoted
} from '../../core/services/voting';
import { storage } from '../../firebase';
import './../../styles/pages/member-voting.scss';
import CandidateCard from './components/CandidateCard';

const MemberVoting = ({ userData }) => {
  const user = userData || getUserFromSessionStorage();
  const userClientType = user?.role || '';
  const userClientId = user?.client_id || '';

  const [activeElection, setActiveElection] = useState(null);
  const [positions, setPositions] = useState([]);
  const [candidates, setCandidates] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [hasVoted, setHasVoted] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [poll, setPoll] = useState({ question: '', answer: '' });

  const loadActiveElection = async () => {
    const res = await getActiveElection();
    const now = new Date();
    const activeElection =
      res.election && new Date(res.election.end_date) >= now
        ? res.election
        : null;
    setActiveElection(activeElection);
    return activeElection;
  };

  const checkIfUserVoted = async electionId => {
    const voted = await hasUserVoted(
      electionId,
      [user.first_name, user.last_name].join(' ')
    );
    setHasVoted(voted);
    return voted;
  };

  const loadPositions = async electionId => {
    const res = await getPositions(userClientType, electionId);
    setPositions(res.positions);
  };

  const loadCandidates = async positionId => {
    const res = await getCandidatesByPosition(positionId);
    const candidatesWithProfileImages = await Promise.all(
      res.candidates.map(async candidate => {
        if (candidate.profile_img) {
          const profileImgRef = ref(
            storage,
            `elections/profile_images/${candidate.profile_img}`
          );
          const profileImgUrl = await getDownloadURL(profileImgRef);
          return { ...candidate, profile_img_url: profileImgUrl };
        } else {
          return { ...candidate, profile_img_url: null };
        }
      })
    );
    setCandidates(prevCandidates => ({
      ...prevCandidates,
      [positionId]: candidatesWithProfileImages
    }));
  };

  useEffect(() => {
    const initialize = async () => {
      const election = await loadActiveElection();
      if (election) {
        const voted = await checkIfUserVoted(election.id);
        if (!voted) {
          await loadPositions(election.id);
          const res = await getPolls(election.id);

          if (res.polls?.length) {
            const [poll] = res.polls;
            setPoll(poll);
          }
        }
      }
      setLoading(false);
    };
    initialize();
  }, []);

  useEffect(() => {
    if (positions.length > 0) {
      positions.forEach(position => {
        loadCandidates(position.id);
      });
    }
  }, [positions]);

  const handleCandidateChange = (positionId, candidateId) => {
    setSelectedCandidates(prevSelected => {
      const currentSelected = prevSelected[positionId] || [];
      const position = positions.find(pos => pos.id === positionId);
      const limit = position.vote_count;
      const candidatesInPosition = candidates[positionId] || [];

      const candidate = candidatesInPosition.find(c => c.id === candidateId);
      const isNoneOption =
        candidate?.client_id && /^[0]+$/.test(candidate.client_id);

      if (currentSelected.includes(candidateId)) {
        return {
          ...prevSelected,
          [positionId]: currentSelected.filter(id => id !== candidateId)
        };
      } else {
        // If selecting "None" option, clear all others
        if (isNoneOption) {
          return {
            ...prevSelected,
            [positionId]: [candidateId]
          };
        }

        // If a "None" option is already selected, don't allow selecting others
        const hasNoneSelected = currentSelected.some(id => {
          const c = candidatesInPosition.find(cand => cand.id === id);
          return c?.client_id && /^[0]+$/.test(c.client_id);
        });

        if (hasNoneSelected) {
          alert(
            'Please unselect the "None" option before selecting other candidates.'
          );
          return prevSelected;
        }

        if (currentSelected.length < limit) {
          return {
            ...prevSelected,
            [positionId]: [...currentSelected, candidateId]
          };
        } else {
          // If limit is 1, replace the selection
          if (limit === 1) {
            return {
              ...prevSelected,
              [positionId]: [candidateId]
            };
          }

          alert(
            `You can only select up to ${limit} candidates for this position.`
          );
          return prevSelected;
        }
      }
    });
  };

  const handleVote = async () => {
    const votes = [];
    for (const positionId in selectedCandidates) {
      const positionTitle = positions.find(pos => pos.id === positionId).title;
      for (const candidateId of selectedCandidates[positionId]) {
        const candidate = candidates[positionId].find(
          c => c.id === candidateId
        );
        votes.push(
          castVote(
            activeElection.id,
            candidateId,
            [user.first_name, user.last_name].join(' '),
            positionTitle,
            candidate.full_name,
            userClientId,
            userClientType,
            null
          )
        );
      }
    }

    votes.push(
      castVote(
        activeElection.id,
        null,
        [user.first_name, user.last_name].join(' '),
        null,
        null,
        userClientId,
        userClientType,
        poll
      )
    );

    await Promise.all(votes);
    alert('Votes cast successfully!');
    setSelectedCandidates({});
    setHasVoted(true);
    setOpenConfirmDialog(false);
  };

  const handleConfirmDialogOpen = () => {
    if (!poll?.answer && poll?.question)
      return alert('Please answer the poll to proceed.');
    // Enable confirm logic if needed
    // if (!allPositionsSelected()) return alert('Please select candidates for all positions.');

    setOpenConfirmDialog(true);
  };

  const handleConfirmDialogClose = () => {
    setOpenConfirmDialog(false);
  };

  const isElectionActive = () => {
    const now = new Date();
    const isActive =
      activeElection &&
      new Date(activeElection.start_date + ' 00:00:00') <= now &&
      new Date(activeElection.end_date + ' 23:59:59') >= now;

    return isActive;
  };

  function sortByName(arr) {
    return arr?.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }

  const handlePollChange = (e, value) => {
    setPoll(prevPoll => ({
      ...prevPoll,
      answer: value
    }));
  };

  return (
    <div className="member-voting">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1 }}
          >
            {loading ? (
              <CircularProgress size={40} thickness={4} />
            ) : activeElection ? (
              activeElection.title
            ) : (
              'No Active Election'
            )}
          </Typography>
          {activeElection && !loading && (
            <Typography variant="subtitle1" color="text.secondary">
              Please cast your vote carefully. Changes cannot be made after
              submission.
            </Typography>
          )}
        </Box>

        {hasVoted ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: '#f8f9fa',
              borderRadius: 4
            }}
          >
            <Typography
              variant="h5"
              color="success.main"
              sx={{ fontWeight: 600 }}
            >
              ✓ You have successfully cast your votes.
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Thank you for participating in the election.
            </Typography>
          </Paper>
        ) : (
          <>
            {positions.map(
              position =>
                position.client_types.includes(userClientType) && (
                  <Box key={position.id} sx={{ mb: 6 }}>
                    <Box
                      sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: '#35b258' }}
                      >
                        {position.title}
                      </Typography>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        Select {position.vote_count}
                      </Box>
                    </Box>

                    {candidates[position.id] ? (
                      <Grid container spacing={3}>
                        {sortByName(candidates[position.id]).map(candidate => (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            key={candidate.id}
                          >
                            <CandidateCard
                              candidate={candidate}
                              selected={
                                selectedCandidates[position.id]?.includes(
                                  candidate.id
                                ) || false
                              }
                              disabled={(() => {
                                const currentSelected =
                                  selectedCandidates[position.id] || [];
                                const hasNoneSelected = currentSelected.some(
                                  id => {
                                    const c = candidates[position.id]?.find(
                                      cand => cand.id === id
                                    );
                                    return (
                                      c?.client_id && /^[0]+$/.test(c.client_id)
                                    );
                                  }
                                );
                                const isThisNone =
                                  candidate.client_id &&
                                  /^[0]+$/.test(candidate.client_id);
                                return hasNoneSelected && !isThisNone;
                              })()}
                              onSelect={() =>
                                handleCandidateChange(position.id, candidate.id)
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        No candidates available for this position.
                      </Typography>
                    )}
                  </Box>
                )
            )}

            {!hasVoted && activeElection && (
              <Box
                component={Paper}
                elevation={3}
                sx={{ p: 4, mt: 4, borderRadius: 3, bgcolor: '#fff' }}
              >
                {poll?.question && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {poll.question}
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        aria-label="poll"
                        name="poll-radio-group"
                        value={poll.answer || ''}
                        onChange={handlePollChange}
                      >
                        {poll.options?.split(',').map((option, index) => (
                          <FormControlLabel
                            key={index}
                            value={option}
                            control={
                              <Radio
                                sx={{
                                  color: '#35b258',
                                  '&.Mui-checked': { color: '#35b258' }
                                }}
                              />
                            }
                            label={option}
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <Divider sx={{ my: 3 }} />
                  </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleConfirmDialogOpen}
                    disabled={!isElectionActive()}
                    sx={{
                      bgcolor: '#35b258',
                      px: 5,
                      py: 1.5,
                      borderRadius: '50px',
                      fontSize: '1rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 8px 16px rgba(53, 178, 88, 0.24)',
                      '&:hover': {
                        bgcolor: '#2e964b',
                        boxShadow: '0 12px 20px rgba(53, 178, 88, 0.32)'
                      },
                      width: '100%'
                    }}
                  >
                    Submit Votes
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Container>

      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', pb: 1 }}>
          Confirm Your Votes
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mb: 3 }}
          >
            Please review your selections. Once submitted, you cannot vote
            again.
          </Typography>

          <List disablePadding>
            {Object.keys(selectedCandidates).map(positionId => {
              if (
                !selectedCandidates[positionId] ||
                selectedCandidates[positionId].length === 0
              )
                return null;

              return (
                <Box
                  key={positionId}
                  sx={{ mb: 2, bgcolor: '#f8f9fa', p: 2, borderRadius: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    color="#35b258"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {positions.find(pos => pos.id === positionId)?.title}
                  </Typography>
                  {selectedCandidates[positionId].map(candidateId => {
                    const candidate = candidates[positionId]?.find(
                      c => c.id === candidateId
                    );
                    return (
                      <Typography
                        key={candidateId}
                        variant="body1"
                        sx={{ ml: 2, display: 'flex', alignItems: 'center' }}
                      >
                        • {candidate?.full_name}
                      </Typography>
                    );
                  })}
                </Box>
              );
            })}

            {poll?.answer && (
              <Box sx={{ mt: 2, bgcolor: '#f8f9fa', p: 2, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="#35b258"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  Poll Question
                </Typography>
                <Typography variant="body1" sx={{ ml: 2 }}>
                  {poll.answer}
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
          <Button
            onClick={handleConfirmDialogClose}
            sx={{ color: '#757575', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVote}
            variant="contained"
            sx={{
              bgcolor: '#35b258',
              borderRadius: '50px',
              px: 4,
              '&:hover': { bgcolor: '#2e964b' }
            }}
          >
            Confirm & Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MemberVoting;
